import { Button, Stack } from '@mui/material';
import { ProjectStage } from '@provision/entity-types';
import { useCallback } from 'react';
import { CopyToClipboardButton } from 'src/components';
import { ProjectMenu } from 'src/components/ProjectComponents/project-menu';
import { useProjectActions } from 'src/components/SidePanelTabs/Projects/use-project-actions';
import { formatProjectDate } from 'src/components/SidePanelTabs/Projects/use-project-card-form';
import { hasChanges } from 'src/components/SidePanelTabs/Projects/utils';
import { SiteRelatedTabs } from 'src/components/SidePanelTabs/SiteTab/site-related-tabs';
import { SiteTab } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SiteShareButton } from 'src/components/SiteComponents/site-share-button';
import { Address, Project, useProject } from 'src/graphql';
import { SectionHeader } from 'src/pages/Conversations/SidePanel/section-header';
import { useUpdateAssociatedSite } from 'src/pages/Sites/use-update-associated-site';
import { useScreenDimensions } from 'src/utils/system';
import { ProjectFormValues } from './types';
import { useProjectForm } from './use-project-form';

export const ProjectCard = ({
  project,
  variant = 'standard',
  onDelete,
}: {
  project: Project;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  onDelete?: () => void;
}) => {
  const address = project.address as Address;

  const { isMobile } = useScreenDimensions();

  const { projectFormFields } = useProjectForm(project, address);

  const updateAssociatedSite = useUpdateAssociatedSite(address);

  const { updateProject } = useProject(project?.id);

  const { submitDay1, proposalWon, proposalLost } = useProjectActions({ project, address });

  const onSubmitAssociatedSite = useCallback(
    async (values: ProjectFormValues) => {
      const promises: Promise<unknown>[] = [];

      const projectStage = values.projectStage?.id
        ? ({
            name: values.projectStage?.label || '',
            id: Number(values.projectStage?.id || project?.projectStageId),
          } as Partial<ProjectStage> as ProjectStage)
        : project?.projectStage;
      const projectUpdate = {
        name: values.projectName || project?.name,
        projectStage,
        projectStageId: Number(values.projectStage?.id || project?.projectStageId),
        day1StartDate: formatProjectDate(values.day1Date) || project?.day1StartDate,
        estimatedDay1Date:
          formatProjectDate(values.estimatedDay1) || project?.estimatedDay1Date,
        signingDate: formatProjectDate(values.estimatedSigning) || project?.signingDate,
        warrantyExpirationDate:
          formatProjectDate(values.warrantyExpiration) || project?.warrantyExpirationDate,
        projectLostReasonId: values.projectLostReason?.id || project?.projectLostReasonId,
        lostReasonNotes: project?.lostReasonNotes || '',
      };

      if (hasChanges(projectUpdate, project as Record<string, unknown>)) {
        promises.push(
          updateProject({
            ...projectUpdate,
            addressId: address?.addressId,
          }),
        );
      }

      promises.push(updateAssociatedSite(values));

      if (values.projectManager) {
        promises.push(
          updateProject({
            ownerId: values.projectManager.id,
          }),
        );
      }

      await Promise.all(promises);
    },
    [address?.addressId, project, updateAssociatedSite, updateProject],
  );

  return address ? (
    <SiteTab
      address={address}
      onSubmit={onSubmitAssociatedSite}
      fields={projectFormFields}
      hideAddressRow
      propertiesHeader={
        <>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-end"
            paddingBottom={0.75}
          >
            <CopyToClipboardButton content={address.fullAddress!} />
            <SiteShareButton
              address={address}
              partnerId={address?.partner?.partnerId ?? address?.partnerId}
            />
            {isMobile ? (
              <ProjectMenu address={address} project={project} onDelete={onDelete} />
            ) : null}
          </Stack>
          <SectionHeader
            title="Properties"
            sx={{
              lineHeight: 1.2,
              marginTop: 0.25,
              padding: (theme) => theme.spacing(0.75, 0, 1),
            }}
          />
        </>
      }
    >
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap={2}
        sx={{
          maxWidth: 'min(100%, 440px)',
        }}
      >
        {!!proposalLost.isAllowed && (
          <Button color="error" variant="contained" fullWidth onClick={proposalLost.handler}>
            Mark as Lost
          </Button>
        )}
        {!!submitDay1.isAllowed && (
          <Button color="success" variant="contained" fullWidth onClick={submitDay1.handler}>
            {submitDay1.label}
          </Button>
        )}
        {!!proposalWon.isAllowed && (
          <Button color="success" variant="contained" fullWidth onClick={proposalWon.handler}>
            Won Proposal
          </Button>
        )}
      </Stack>
      <SiteRelatedTabs address={address} project={project} variant={variant} />
    </SiteTab>
  ) : null;
};
